import React from "react";
import { useRoutes, Outlet, Navigate } from "react-router-dom";
import { PERMISSION_KEYS } from "./utils/permissionsConstants";
import LandingPage from "./pages";
import Login from "./pages/auth/login";
import ForgotPassword from "./pages/auth/forgot-password";
import ResetPassword from "./pages/auth/reset-password";
import ProtectedRoute from "./ProtectedRoute";
import SessionProtectedRoute from "./SessionProtectedRoute";
import PartnerSessionProtectedRoute from "./PartnerSessionProtectedRoute";
import AdminPortalProtectedRoute from "./AdminPortalProtectedRoute";
import { PARTNER_PORTAL_PERMISSION_KEYS as PP_KEYS, PARTNER_PROJECT_ACCESS_KEYS } from "./utils/permissions/partnerPortalPermissionKeys";
import {
  PARTNER_MANAGEMENT_PERMISSION_KEYS as PM_KEYS,
  PARTNER_ORGANIZATION_ACCESS_KEYS,
} from "./utils/permissions/partnerManagementPermissionKeys";
import { SALES_ENGAGEMENT_PERMISSION_KEYS as SE_KEYS } from "./utils/permissions/salesEngagementPermissionKeys";
import { REPORTS_DASHBOARD_PERMISSION_KEYS as RD_KEYS } from "./utils/permissions/reportsDashboardPermissionKeys";
import {
  SETTINGS_AREA_PERMISSION_KEYS,
  SETTINGS_PERMISSION_KEYS as SETTINGS_KEYS,
} from "./utils/permissions/settingsPermissionKeys";
import PartnerDashboard from "./pages/partner/dashboard";
import PartnerHome from "./pages/partner/dashboard/home";
import PartnerServices from "./pages/partner/dashboard/services";
import PartnerSettings from "./pages/partner/dashboard/settings";
import PartnerMyAccount from "./pages/partner/my-account";
import HIDAdminHome from "./pages/hid-admin/home";
import HIDAdminDashboard from "./pages/hid-admin/dashboard";
import CreateNewPartner from "./pages/hid-admin/create-new-partner";
import PartnerList from "./pages/hid-admin/dashboard/partner-organizations/partner-list";
import ManagePartner from "./pages/hid-admin/dashboard/partner-organizations/manage-partner";
import PartnerDetails from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/partner-details";
import ServicesAndSubscriptions from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/services-and-subscriptions";
import CreateExternalContract from "./pages/hid-admin/external-contract/create-external-contract";
import UpdateExternalContract from "./pages/hid-admin/external-contract/update-external-contract";
import OrdersManagePartner from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/orders";
import PartnerUsersAdmin from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/partner-users";
import HIDAdminMyAccount from "./pages/hid-admin/my-account";
import ActivateAccount from "./pages/auth/activate-account";
import SuspenseFallback from "./pages/suspense-fallback";
import HistoryLog from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/history-log";
import RolesAndPermissions from "./pages/hid-admin/dashboard/roles-and-permissions";
import EngagementForm from "./pages/hid-admin/engagement-form/create-engagement";
import UpdateEngagementForm from "./pages/hid-admin/engagement-form/update-engagement";
import EngagementList from "./pages/hid-admin/dashboard/engagement-list";
import SystemLogs from "./pages/hid-admin/dashboard/system-logs";
import ProjectOnboarding from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/project-onboarding";
import ManagePartnerDashboard from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/partner-dashboard";
import Notes from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/notes";
import SalesPartnerNoteForm from "./pages/hid-admin/sales-partner-note-form";
import PartnerProjects from "./pages/partner/dashboard/projects";
import ProjectDetails from "./pages/partner/dashboard/projects/project-details";
import PartnerChatbot from "./pages/partner/chatbot";
import PartnerProgramSettings from "./pages/hid-admin/dashboard/partner-settings";
import ReportsDashboard from "./pages/hid-admin/dashboard/reports-dashboard";
import ValidationStatus from "./pages/hid-admin/dashboard/validation-status";
import DashboardList from "./pages/hid-admin/poc-dashboard/partner-list";
import FeatureList from "./pages/hid-admin/poc-dashboard/partner-list/feature-list";
import IncidentList from "./pages/hid-admin/poc-dashboard/partner-list/incident-list";
import HealthCheckUPList from "./pages/hid-admin/poc-dashboard/health-checkup";
import HealthCheckHistory from "./pages/hid-admin/poc-dashboard/health-checkup/HealthCheckHistory";
import FeatureComparison from "./pages/hid-admin/poc-dashboard/partner-list/feature-comparison";
import PartnerPOCDashboard from "./pages/partner/poc-dashboard";
import POCDashboard from "./pages/hid-admin/poc-dashboard";
import FeatureManagement from "./pages/hid-admin/poc-dashboard/hid-users";
import ReportsDetailPage from "./pages/hid-admin/dashboard/reports-dashboard/report-detail";
import IntegrationValidation from "./pages/partner/dashboard/projects/project-details/validation";
import ValidationHistoryList from "./pages/partner/dashboard/projects/project-details/validation/components/ValidationHistoryList/index";
import ValidationHistoryDetail from "./pages/partner/dashboard/projects/project-details/validation/components/ValidationHistoryDetail/index";
import PartnerProgram from "./pages/hid-admin/dashboard/partner-organizations/manage-partner/partner-program";
import PartnerProgramPartnerView from "./pages/partner/dashboard/partner-program";
import PartnerBenefitsComparison from "./pages/partner/dashboard/partner-program/benefits-comparison";
import ReportsDetailParent from "./pages/hid-admin/dashboard/reports-dashboard/report-detail/ReportDetailParent";
import FeatureComparisonTable from "./pages/resources/feature-comparison-table";
import ResourcesLandingPage from "./pages/resources";

const PartnerLandingPage = React.lazy(() => import("./pages/partner"));
const HIDAdminLandingPage = React.lazy(() => import("./pages/hid-admin"));

const AppRoutes: React.FC<{
  authenticationStatus: boolean | Object;
}> = ({ authenticationStatus }) => {
  const routes = [
    {
      path: "/",
      element: (
        <ProtectedRoute
          element={<LandingPage />}
          authenticationStatus={authenticationStatus}
        />
      ),
      children: [
        {
          path: "partner/:partnerID",
          element: (
            <React.Suspense fallback={<SuspenseFallback />}>
              <PartnerLandingPage />
            </React.Suspense>
          ),
          children: [
            {
              path: "dashboard",
              element: <PartnerDashboard />,
              children: [
                {
                  path: "home",
                  element: (
                    <PartnerSessionProtectedRoute
                      element={<PartnerHome />}
                      requiredPermissionKey={PP_KEYS.PARTNER_PORTAL_DASHBOARD}
                    />
                  ),
                },
                {
                  path: "services",
                  element: (
                    <PartnerSessionProtectedRoute
                      element={<PartnerServices />}
                      requiredPermissionKey={PP_KEYS.VIEW_SERVICE_CATALOG}
                    />
                  ),
                },
                {
                  path: "projects",
                  element: (
                    <PartnerSessionProtectedRoute
                      element={<PartnerProjects />}
                      requiredPermissionKeys={PARTNER_PROJECT_ACCESS_KEYS}
                    />
                  ),
                },
                {
                  path: "projects/:projectName",
                  element: (
                    <PartnerSessionProtectedRoute
                      element={<ProjectDetails />}
                      requiredPermissionKeys={PARTNER_PROJECT_ACCESS_KEYS}
                    />
                  ),
                  children: [
                    {
                      path: "validation/:integrationId",
                      element: (
                        <PartnerSessionProtectedRoute
                          element={<IntegrationValidation />}
                          requiredPermissionKeys={PARTNER_PROJECT_ACCESS_KEYS}
                        />
                      ),
                      children: [
                        {
                          path: "history",
                          element: (
                            <PartnerSessionProtectedRoute
                              element={<ValidationHistoryList />}
                              requiredPermissionKeys={PARTNER_PROJECT_ACCESS_KEYS}
                            />
                          ),
                        },
                        {
                          path: "history/:entryId",
                          element: (
                            <PartnerSessionProtectedRoute
                              element={<ValidationHistoryDetail />}
                              requiredPermissionKeys={PARTNER_PROJECT_ACCESS_KEYS}
                            />
                          ),
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "settings",
                  element: <PartnerSettings />,
                },
                {
                  path: "partner-program",
                  element: (
                    <PartnerSessionProtectedRoute
                      element={<PartnerProgramPartnerView />}
                      requiredPermissionKey={PP_KEYS.PARTNER_PROGRAM_MANAGEMENT}
                    />
                  ),
                },
                {
                  path: "partner-program/benefits-comparison",
                  element: (
                    <PartnerSessionProtectedRoute
                      element={<PartnerBenefitsComparison />}
                      requiredPermissionKey={PP_KEYS.PARTNER_PROGRAM_MANAGEMENT}
                    />
                  ),
                },
              ],
            },
            {
              path: "poc-dashboard",
              element: <PartnerPOCDashboard />,
              children: [
                {
                  path: "feature-list",
                  element: <FeatureList />,
                },
                {
                  path: "incident-list",
                  element: <IncidentList />,
                },
                {
                  path: "health-checkup",
                  element: <HealthCheckUPList />,
                },
                {
                  path: "health-check-history",
                  element: <HealthCheckHistory />,
                },
                {
                  path: "feature-comparison",
                  element: <FeatureComparison />,
                },
              ],
            },
            {
              path: "my-account",
              element: <PartnerMyAccount />,
            },
            {
              path: "chatbot",
              element: <PartnerChatbot />,
            },
            // {
            //   path: "project-details",
            //   element: <ProjectDetails />,
            // },
          ],
        },
        {
          path: "hid-admin",
          element: (
            <AdminPortalProtectedRoute
              element={
                <React.Suspense fallback={<SuspenseFallback />}>
                  <HIDAdminLandingPage />
                </React.Suspense>
              }
            />
          ),
          children: [
            {
              element: <HIDAdminDashboard />,
              children: [
                {
                  path: "home",
                  element: <HIDAdminHome />,
                },
                {
                  path: "dashboard",
                  element: <Outlet />,
                  children: [
                    {
                      path: "home",
                      element: <Navigate to="/hid-admin/home" replace />,
                    },
                    {
                      path: "partner-organizations",
                      element: <PartnerList />,
                      children: [
                    {
                      path: ":ID",
                      element: (
                        <SessionProtectedRoute
                          element={<ManagePartner />}
                          requiredPermissionKey={PM_KEYS.PARTNER_CONFIGURATIONS}
                          requiredPermissionKeys={PARTNER_ORGANIZATION_ACCESS_KEYS}
                        />
                      ),
                      children: [
                        {
                          path: "dashboard",
                          element: (
                            <SessionProtectedRoute
                              element={<ManagePartnerDashboard />}
                              requiredPermissionKey={PM_KEYS.PARTNER_ADMIN_DASHBOARD}
                            />
                          ),
                        },
                        {
                          path: "partner-details",
                          element: <PartnerDetails />,
                        },
                        {
                          path: "services-and-subscriptions",
                          element: <ServicesAndSubscriptions />,
                        },
                        {
                          path: "orders",
                          element: <OrdersManagePartner />,
                        },
                        {
                          path: "partner-users",
                          element: (
                            <SessionProtectedRoute
                              element={<PartnerUsersAdmin />}
                              requiredPermissionKey={PM_KEYS.MANAGE_PARTNER_USER}
                            />
                          ),
                        },
                        {
                          path: "project-onboarding",
                          element: <ProjectOnboarding />,
                        },
                        {
                          path: "notes",
                          element: <Notes />,
                        },
                          {
                          path: "partner-program",
                          element: <PartnerProgram />,
                        },
                        {
                          path: "history-log",
                          element: <HistoryLog />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: "reports-dashboard",
                  children: [
                    {
                      index: true, // default /reports-dashboard
                      element: (
                        <SessionProtectedRoute
                          element={<ReportsDashboard />}
                          requiredPermissionKey={RD_KEYS.CATEGORY_MANAGEMENT}
                        />
                      ),
                    },
                    {
                      path: "category/:categoryKey",
                      element: (
                        <SessionProtectedRoute
                          element={<ReportsDashboard />}
                          requiredPermissionKey={RD_KEYS.CATEGORY_MANAGEMENT}
                        />
                      ),
                    },
                    {
                      path: "category/:categoryKey/report/:reportId",
                      element: (
                        <SessionProtectedRoute
                          element={<ReportsDetailParent />}
                          requiredPermissionKey={RD_KEYS.CATEGORY_MANAGEMENT}
                        />
                      ),
                    },
                  ],
                },
                {
                  path: "validation-status",
                  element: <ValidationStatus />,
                },
                {
                  path: "hid-users",
                  element: (
                    <Navigate
                      to="/hid-admin/dashboard/partner-settings?module=user-management"
                      replace
                    />
                  ),
                },
                {
                  path: "partner-settings",
                  element: (
                    <SessionProtectedRoute
                      element={<PartnerProgramSettings />}
                      requiredPermissionKey={
                        SETTINGS_KEYS.PARTNER_PROGRAM_SETTINGS
                      }
                      requiredPermissionKeys={[
                        ...SETTINGS_AREA_PERMISSION_KEYS,
                      ]}
                    />
                  ),
                },
                {
                  path: "engagement-list",
                  element: (
                    <SessionProtectedRoute
                      element={<EngagementList />}
                      requiredPermissionKey={SE_KEYS.MANAGE_ENGAGEMENTS}
                    />
                  ),
                },
                {
                  path: "system-logs",
                  element: (
                    <SessionProtectedRoute
                      element={<SystemLogs />}
                      requiredPermissionKey={SETTINGS_KEYS.USER_SETTINGS}
                    />
                  ),
                },
              ],
            },
          ],
        },
            {
              path: "create-new-partner",
              element: (
                <SessionProtectedRoute
                  element={<CreateNewPartner />}
                  requiredPermissionKey={PM_KEYS.PARTNER_CONFIGURATIONS}
                  requireEdit
                />
              ),
            },
            {
              path: "create-external-contract/:partnerID",
              element: (
                <SessionProtectedRoute
                  element={<CreateExternalContract />}
                  requiredPermissionKey={PM_KEYS.CONTRACT_MANAGEMENT}
                />
              ),
            },
            {
              path: "update-external-contract/:ID",
              element: (
                <SessionProtectedRoute
                  element={<UpdateExternalContract />}
                  requiredPermissionKey={PM_KEYS.CONTRACT_MANAGEMENT}
                />
              ),
            },
            {
              path: "my-account",
              element: <HIDAdminMyAccount />,
            },
            {
              path: "engagement-form",
              element: (
                <SessionProtectedRoute
                  element={<EngagementForm />}
                  requiredPermissionKey={SE_KEYS.SUBMIT_ENGAGEMENT_FORM}
                  requireEdit
                />
              ),
            },
            {
              path: "engagement-form/update/:engagementId",
              element: (
                <SessionProtectedRoute
                  element={<UpdateEngagementForm />}
                  requiredPermissionKey={SE_KEYS.SUBMIT_ENGAGEMENT_FORM}
                />
              ),
            },
            {
              path: "sales-partner-note-form/:partnerID",
              element: (
                <SessionProtectedRoute
                  element={<SalesPartnerNoteForm />}
                  requiredPermissionKey={PM_KEYS.PARTNER_NOTES}
                />
              ),
            },
            {
              path: "feature-comparison-table",
              element: <Navigate to="/resources/feature-comparison-table" replace />,
            },
            // {
            //   path: "project-timeline/:projectTechnologyID",
            //   element: <ProjectTimeline />,
            // },
          ],
        },
        //poc partner portal
        {
          path: "hid-partner-admin",
          element: (
            <AdminPortalProtectedRoute
              element={
                <React.Suspense fallback={<SuspenseFallback />}>
                  <HIDAdminLandingPage />
                </React.Suspense>
              }
            />
          ),
          children: [
            {
              path: "home",
              element: <Navigate to="/hid-admin/home" replace />,
            },
            {
              path: "dashboard",
              element: <POCDashboard />,
              children: [
                {
                  path: "dashboard-list",
                  element: <DashboardList />,
                },

                {
                  path: "feature-management",
                  element: <FeatureList />,
                },
                {
                  path: "roles-and-permissions",
                  element: (
                    <SessionProtectedRoute
                      element={<RolesAndPermissions />}
                      requiredPermissionKey={SETTINGS_KEYS.USER_SETTINGS}
                    />
                  ),
                },
                {
                  path: "incident-list",
                  element: <IncidentList />,
                },
                {
                  path: "feature-comparison",
                  element: <FeatureComparison />,
                },
                {
                  path: "health-checkup",
                  element: <HealthCheckUPList />,
                },
                {
                  path: "health-check-history",
                  element: <HealthCheckHistory />,
                },
                {
                  path: "system-logs",
                  element: (
                    <SessionProtectedRoute
                      element={<SystemLogs />}
                      requiredPermissionKey={SETTINGS_KEYS.USER_SETTINGS}
                    />
                  ),
                },
              ],
            },
          ],
        },

        {
          path: "resources",
          element: <ResourcesLandingPage />,
          children: [
            {
              path: "feature-comparison-table",
              element: <FeatureComparisonTable />,
            },
          ],
        },

        { path: "*", element: <Navigate to="/" /> },
      ],
    },
    { path: "/login", element: <Login /> },
    { path: "/forgot-password", element: <ForgotPassword /> },
    {
      path: "/reset-password/:customerId/:resetToken",
      element: <ResetPassword />,
    },
    {
      path: "/account-activate/:customerId/:activationToken",
      element: <ActivateAccount />,
    },
  ];

  return useRoutes(routes);
};

export default AppRoutes;
